import { z } from 'zod'

export const userSchema = z.object({
    id: z.number().optional(),
    name: z.string().min(2, 'Name must be at least 2 characters'),
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters').optional(),
    roles: z.array(z.number()).min(1, 'At least one role is required'),
    teams: z.array(z.number()).min(1, 'At least one team is required'),
    primaryTeamId: z.number({ required_error: 'A primary team must be selected' }),
    isActive: z.boolean().default(true),
    standby: z.boolean().default(false),
    phoneCode: z.string().optional().nullable(),
    phoneNumber: z.string().optional().nullable(),
    mfaRequired: z.boolean().default(false),
    mfaMethods: z.array(z.enum(['totp', 'webauthn'])).default([]),
    language: z.string().default('en-US'),
    timezone: z.string().default('UTC'),
    location: z.number().int().positive().nullable().optional(),
    avatarUrl: z.union([z.string(), z.literal(''), z.undefined(), z.null()]).optional(),
    avatarColor: z.union([z.string(), z.literal(''), z.undefined(), z.null()]).optional(),
})

// Schema for creating new users (password required)
export const createUserSchema = userSchema
    .extend({
        password: z.string().min(8, 'Password must be at least 8 characters'),
    })
    .refine(
        (data) => {
            // Primary team must be in the teams array
            return !data.primaryTeamId || data.teams.includes(data.primaryTeamId)
        },
        {
            message: 'Primary team must be one of the selected teams',
            path: ['primaryTeamId'],
        }
    )

// Schema for updating users (password optional)
export const updateUserSchema = userSchema
    .omit({ password: true })
    .extend({
        password: z.string().optional().or(z.literal('')),
    })
    .refine(
        (data) => {
            // Primary team must be in the teams array
            return !data.primaryTeamId || data.teams.includes(data.primaryTeamId)
        },
        {
            message: 'Primary team must be one of the selected teams',
            path: ['primaryTeamId'],
        }
    )

export type UserFormData = z.infer<typeof userSchema>

export const roleSchema = z.object({
    id: z.number().optional(),
    name: z.string().min(2, 'Name must be at least 2 characters'),
    description: z.string().optional(),
    permissions: z.array(z.number()).default([]),
})

export type RoleFormData = z.infer<typeof roleSchema>

// Available roles with namespaced permissions
export const availableRoles = [
    {
        id: 1,
        name: 'admin',
        description: 'Full system access',
        permissions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] // All scheduler permissions
    },
    {
        id: 2,
        name: 'user',
        description: 'Standard user access',
        permissions: [1, 4, 5, 7] // vaults:read, jobs:read, jobs:write, users:read
    },
    {
        id: 3,
        name: 'viewer',
        description: 'Read-only access',
        permissions: [1, 4, 7] // vaults:read, jobs:read, users:read
    },
] as const

// Available permissions (namespaced for scheduler)
export const availablePermissions = [
    { id: 1, name: 'scheduler:vaults:read', resource: 'vaults', action: 'read' },
    { id: 2, name: 'scheduler:vaults:write', resource: 'vaults', action: 'write' },
    { id: 3, name: 'scheduler:vaults:delete', resource: 'vaults', action: 'delete' },
    { id: 4, name: 'scheduler:jobs:read', resource: 'jobs', action: 'read' },
    { id: 5, name: 'scheduler:jobs:write', resource: 'jobs', action: 'write' },
    { id: 6, name: 'scheduler:jobs:delete', resource: 'jobs', action: 'delete' },
    { id: 7, name: 'scheduler:users:read', resource: 'users', action: 'read' },
    { id: 8, name: 'scheduler:users:write', resource: 'users', action: 'write' },
    { id: 9, name: 'scheduler:users:delete', resource: 'users', action: 'delete' },
    { id: 10, name: 'scheduler:policies:read', resource: 'policies', action: 'read' },
    { id: 11, name: 'scheduler:policies:write', resource: 'policies', action: 'write' },
    { id: 12, name: 'scheduler:policies:delete', resource: 'policies', action: 'delete' },
] as const



